'use client';
import { useState, useEffect, useRef } from 'react';
import { Metadata } from 'next';
import Image from 'next/image';
import PageContainer from '@/layout/page-container';
import Section from '@/layout/section';
import { PageHero, TestimonialCards } from '@/modules/marketing/components/page-shared';
import { Animated } from '@/components/ui/animated';

const sampleTestimonials = [
  { quote: 'Very proactive and professional company. Communication was easy and straight forward. They work hard to get you the best clinical experience. They saved me from being dropped from the class by being fast in finding additional clinical placement. I\'m very thankful for their support.', name: 'Maja C. PMHNP' },
  { quote: 'I had an outstanding experience with ACE! They were incredibly professional and supportive from start to finish. Their team was responsive to all my queries, provided clear instructions, and helped me feel confident throughout the journey. Thanks to their efforts, I had a successful clinical experience that was both educational and enriching.', name: 'Dr. Anosh F.' },
  { quote: 'This company is the perfect location to relieve your clinical placement process stress. If you want to avoid unnecessary anxiety and using your time wisely, CALL THIS GROUP! (Jon helped me find my current and future placement).', name: 'Lori B. FNP' },
];

const carouselImages = [
  { src: '/images/testimonial-1.jpg', alt: 'ACE Rotations Clinical Experience' },
  { src: '/images/testimonial-2.jpg', alt: 'ACE Rotations Student Success' },
  { src: '/images/testimonial-3.jpg', alt: 'ACE Rotations Team' },
  { src: '/images/testimonial-4.jpg', alt: 'ACE Rotations Learning Environment' },
];

export default function TestimonialsPage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const resetTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
    }, 3000);
  };

  // Auto-advance carousel every 3 seconds
  useEffect(() => {
    resetTimer();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
    resetTimer();
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + carouselImages.length) % carouselImages.length);
    resetTimer();
  };

  return (
    <PageContainer>
      <Animated animation="fade-up" duration={800}>
        <PageHero
          kicker="Outcomes"
          title="Previous Students Experiences"
          subtitle="See what our previous students had to say about their experiences with us. Some of them are active preceptors now too."
        />
      </Animated>
      <Section>
        <div className="space-y-14">
          <div className="space-y-6">
            <Animated animation="fade-up" delay={100}>
              <h2 className="text-xl font-semibold tracking-tight">Highlights</h2>
            </Animated>
            <Animated animation="fade-up" delay={200}>
              <TestimonialCards items={sampleTestimonials} />
            </Animated>
          </div>
          
          {/* Carousel */}
          <Animated animation="scale" delay={300}>
            <div className="relative max-w-5xl mx-auto">
              <div className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                {carouselImages.map((img, index) => (
                  <Image
                    key={img.src}
                    src={img.src}
                    alt={img.alt}
                    fill
                    className={`object-cover transition-opacity duration-500 ${
                      index === currentSlide ? 'opacity-100' : 'opacity-0'
                    }`}
                    sizes="(max-width: 1280px) 100vw, 1280px"
                    priority={index === 0}
                  />
                ))}
              </div>
              
              {/* Navigation Buttons */}
              <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/90 hover:bg-white shadow-lg flex items-center justify-center transition-all hover:scale-110 active:scale-95"
                aria-label="Previous image"
              >
                <svg className="w-6 h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/90 hover:bg-white shadow-lg flex items-center justify-center transition-all hover:scale-110 active:scale-95"
                aria-label="Next image"
              >
                <svg className="w-6 h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              
              {/* Dots Indicator */}
              <div className="flex justify-center gap-2 mt-6">
                {carouselImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => { setCurrentSlide(index); resetTimer(); }}
                    className={`h-2.5 rounded-full transition-all duration-300 ${
                      index === currentSlide 
                        ? 'bg-blue-600 w-8' 
                        : 'bg-gray-300 hover:bg-gray-400 w-2.5'
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </Animated>
        </div>
      </Section>
    </PageContainer>
  );
}
